import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "./styles/variables.module.css";
import globals from "./styles/globals.module.css";
import { Page, Uid } from "./types";
import StartPage from "./components/StartPage";
import SignUpPage from "./components/SignUpPage";
import GroupPage from "./components/GroupPage";
import LoginPage from "./components/LoginPage";
import MainPage from "./components/MainPage";
import WelcomePage from "./components/WelcomePage";
import { initializeApp } from "firebase/app";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { UserIdContext } from "./hooks/useUserId";
import { NavigationContext } from "./hooks/useNavigate";
import LineupDraftnamePage from "./components/LineupDraftnamePage";
import HowToPlayPage from "./components/HowToPlayPage";
import LineupSelectionPage from "./components/LineupSelectionPage";
import SettingsPage from "./components/SettingsPage";
import LeaderboardSelectionPage from "./components/LeaderboardSelectionPage";
import NewsPage from "./components/NewsPage";
import NewsArticlePage from "./components/NewsArticle";
import { NewsArticleContext, TOTAL_ARTICLES } from "./hooks/useNewsArticle";
import ExpandedLeaderboardPage from "./components/ExpandedLeaderboardPage";
import { CurrentLineupIndexContext } from "./hooks/lineups";
import { useMe } from "./hooks/auth";
import { AuthResponse } from "./api/types";

const firebaseConfig = {
  apiKey: "AIzaSyBMz0omsFAc6e8dldhsaw8oUpaONMVCW4I",
  authDomain: "band-draft-mvp.firebaseapp.com",
  projectId: "band-draft-mvp",
  storageBucket: "band-draft-mvp.firebasestorage.app",
  messagingSenderId: "1061610413083",
  appId: "1:1061610413083:web:8bb5b28b1b6d730389acbe",
};

initializeApp(firebaseConfig);

const queryClient = new QueryClient();

function SessionRestorer({
  onRestored,
}: {
  onRestored: (playerId: Uid | null) => void;
}) {
  const { data, isPending } = useMe();

  useEffect(() => {
    if (!isPending) {
      onRestored((data?.playerId as Uid) ?? null);
    }
    // TODO: Add error handling here! An unhandled error for the useMe mutation means a server problem.
  }, [data, isPending, onRestored]);

  return null;
}

function App() {
  const [userId, setUserId] = useState<Uid>();
  const [isAuthChecked, setIsAuthChecked] = useState(false);

  const [page, setPage] = useState<Page>(Page.Start);
  const prevPageRef = useRef(page);
  const navigate = useCallback((newPage: Page) => {
    setPage((prev) => {
      prevPageRef.current = prev;
      return newPage;
    });
  }, []);

  const [articleIndex, setArticleIndex] = useState(0);
  const [viewedArticles, setViewedArticles] = useState<Set<number>>(new Set());
  const markAsViewed = useCallback((index: number) => {
    setViewedArticles((prev) => new Set(prev).add(index));
  }, []);
  const unreadCount = TOTAL_ARTICLES - viewedArticles.size;
  const newsArticleValue = useMemo(
    () => ({
      articleIndex,
      setArticleIndex,
      viewedArticles,
      markAsViewed,
      unreadCount,
    }),
    [articleIndex, viewedArticles, markAsViewed, unreadCount],
  );

  const [currentLineupIndex, setCurrentLineupIndex] = useState<number>();
  const currentLineupIndexValue = useMemo(
    () => ({ currentLineupIndex, setCurrentLineupIndex }),
    [currentLineupIndex],
  );

  const [selectedGroupId, setSelectedGroupId] = useState<string>();

  const handleSessionRestored = useCallback((playerId: Uid | null) => {
    setIsAuthChecked(true);
    if (playerId) {
      setUserId(playerId);
      setPage(Page.Welcome);
    }
  }, []);

  const handleLoginSuccess = useCallback((data: AuthResponse) => {
    setUserId(data.playerId as Uid);
    setPage(Page.Welcome);
  }, []);

  const handleSignUpSuccess = useCallback((data: AuthResponse) => {
    setUserId(data.playerId as Uid);
    setPage(Page.Welcome);
  }, []);

  const renderPage = useCallback(
    (p: Page) => {
      switch (p) {
        case Page.Groups:
          return (
            <GroupPage
              selectedGroupId={selectedGroupId}
              setSelectedGroupId={setSelectedGroupId}
            />
          );
        case Page.Welcome:
          const handleWelcomeContinue = () => navigate(Page.LineupSelection);
          return <WelcomePage handleContinue={handleWelcomeContinue} />;
        case Page.Main:
          return (
            <MainPage handleBackButton={() => navigate(Page.LineupSelection)} />
          );
        case Page.LineupDraftname:
          return (
            <LineupDraftnamePage handleLetsDraft={() => navigate(Page.Main)} />
          );
        case Page.HowToPlay:
          return (
            <HowToPlayPage handleBackButton={() => navigate(Page.Welcome)} />
          );
        case Page.LineupSelection:
          return (
            <LineupSelectionPage
              handleNewLineup={(lineupIndex: number) => {
                setCurrentLineupIndex(lineupIndex);
                navigate(Page.LineupDraftname);
              }}
              handleSelectLineup={(lineupIndex: number) => {
                setCurrentLineupIndex(lineupIndex);
                navigate(Page.Main);
              }}
              handleBackButton={() => navigate(Page.Welcome)}
            />
          );
        case Page.News:
          return <NewsPage />;
        case Page.NewsArticle:
          return <NewsArticlePage />;
        case Page.Settings:
          return <SettingsPage />;
        case Page.LeaderboardSelection:
          return (
            <LeaderboardSelectionPage setSelectedGroupId={setSelectedGroupId} />
          );
        case Page.ExpandedLeaderboard:
          return <ExpandedLeaderboardPage selectedGroupId={selectedGroupId} />;
        case Page.None:
        default:
          return <></>;
      }
    },
    [navigate, selectedGroupId],
  );

  return (
    <QueryClientProvider client={queryClient}>
      <div className={globals["App"]}>
        <SessionRestorer onRestored={handleSessionRestored} />
        {isAuthChecked &&
          (userId ? (
            <UserIdContext value={userId}>
              <NavigationContext value={{ navigate, page, prevPageRef }}>
                <NewsArticleContext value={newsArticleValue}>
                  <CurrentLineupIndexContext value={currentLineupIndexValue}>
                    {renderPage(page)}
                  </CurrentLineupIndexContext>
                </NewsArticleContext>
              </NavigationContext>
            </UserIdContext>
          ) : (
            <>
              {page === Page.Login && (
                <LoginPage
                  handleLoginSuccess={handleLoginSuccess}
                  handleBackButton={() => navigate(Page.Start)}
                />
              )}
              {page === Page.SignUp && (
                <SignUpPage
                  handleBackButton={() => navigate(Page.Start)}
                  handleSignUpSuccess={handleSignUpSuccess}
                />
              )}
              {page !== Page.Login && page !== Page.SignUp && (
                <StartPage
                  handleLogin={() => navigate(Page.Login)}
                  handleSignUp={() => navigate(Page.SignUp)}
                />
              )}
            </>
          ))}
        <div
          style={{
            position: "absolute",
            bottom: "10px",
            right: "10px",
          }}
        >
          {process.env.REACT_APP_VERSION}
        </div>
      </div>
    </QueryClientProvider>
  );
}

export default App;
